import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Calendar,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  CreditCard,
  User,
  Building2,
  ArrowRight,
  RefreshCw,
  Plus,
  Eye,
  Download,
  Star,
  MessageSquare,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Review submission state
  const [reviewBooking, setReviewBooking] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, invoicesRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/my/bookings`),
        axios.get(`${API}/my/invoices`),
        axios.get(`${API}/my/reviews`).catch(() => ({ data: [] })),
      ]);
      setBookings(bookingsRes.data);
      setInvoices(invoicesRes.data);
      setReviews(reviewsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const openReviewDialog = (booking) => {
    setReviewBooking(booking);
    setReviewRating(5);
    setReviewComment("");
  };

  const submitReview = async () => {
    if (!reviewBooking) return;
    if (!reviewComment || reviewComment.trim().length < 5) {
      toast.error("Please write at least a few words for your review");
      return;
    }
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        booking_id: reviewBooking.id,
        rating: reviewRating,
        comment: reviewComment.trim(),
      });
      toast.success("Thanks! Your review is pending admin approval.");
      setReviewBooking(null);
      setReviewComment("");
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "overdue":
        return "bg-red-100 text-red-700";
      case "partial":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  const pendingBookings = bookings.filter((b) => b.status === "pending");
  const pendingInvoices = invoices.filter((i) => i.status === "pending" || i.status === "overdue");
  const recurringBookings = bookings.filter((b) => b.is_recurring);
  const completedBookings = bookings.filter(
    (b) => b.status === "completed" || b.status === "paid"
  );
  const reviewedBookingIds = new Set(reviews.map((r) => r.booking_id));
  const reviewableBookings = completedBookings.filter(
    (b) => !reviewedBookingIds.has(b.id)
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1
              className="text-2xl md:text-3xl font-bold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Welcome, {user?.name}
            </h1>
            <div className="flex items-center gap-2 mt-1 text-slate-600">
              {user?.customer_type === "commercial" ? (
                <>
                  <Building2 className="w-4 h-4" />
                  <span>{user?.company_name}</span>
                </>
              ) : (
                <>
                  <User className="w-4 h-4" />
                  <span>Residential Account</span>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button
              asChild
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="book-service-btn"
            >
              <Link to="/booking">
                <Plus className="mr-2 w-4 h-4" />
                Book Service
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-slate-300"
              data-testid="pay-invoice-btn"
            >
              <Link to="/pay-invoice">
                <CreditCard className="mr-2 w-4 h-4" />
                Pay Invoice
              </Link>
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{bookings.length}</p>
                  <p className="text-sm text-slate-600">Total Bookings</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{pendingBookings.length}</p>
                  <p className="text-sm text-slate-600">Pending Services</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{pendingInvoices.length}</p>
                  <p className="text-sm text-slate-600">Unpaid Invoices</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{recurringBookings.length}</p>
                  <p className="text-sm text-slate-600">Recurring Services</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="grid grid-cols-2 gap-1 h-auto w-full sm:inline-flex sm:w-auto sm:h-9 bg-white border border-slate-200 mb-6">
            <TabsTrigger value="bookings" className="w-full" data-testid="tab-bookings">
              My Bookings
            </TabsTrigger>
            <TabsTrigger value="invoices" className="w-full" data-testid="tab-invoices">
              My Invoices
            </TabsTrigger>
            <TabsTrigger value="recurring" className="w-full" data-testid="tab-recurring">
              Recurring Services
            </TabsTrigger>
            <TabsTrigger value="reviews" className="w-full" data-testid="tab-reviews">
              My Reviews
              {reviewableBookings.length > 0 && (
                <Badge className="ml-2 bg-orange-500 text-white">
                  {reviewableBookings.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
              </div>
            ) : bookings.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">No bookings yet</p>
                  <Button asChild className="bg-orange-500 hover:bg-orange-600 text-white">
                    <Link to="/booking">Book Your First Service</Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <Card key={booking.id} className="bg-white" data-testid={`booking-${booking.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900">{booking.service_name}</h3>
                            <Badge className={getStatusColor(booking.status)}>
                              {booking.status}
                            </Badge>
                            {booking.is_recurring && (
                              <Badge className="bg-purple-100 text-purple-700">
                                <RefreshCw className="w-3 h-3 mr-1" />
                                {booking.recurring_frequency}
                              </Badge>
                            )}
                            {booking.crew_name && (
                              <Badge className="bg-blue-100 text-blue-700">
                                <User className="w-3 h-3 mr-1" />
                                {booking.crew_name}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-slate-600 space-y-1">
                            <p>
                              <Calendar className="w-4 h-4 inline mr-2" />
                              {booking.preferred_date} at {booking.preferred_time}
                            </p>
                            <p className="text-slate-500">{booking.address}</p>
                          </div>
                          {/* Photo thumbnails */}
                          {Array.isArray(booking.photo_urls) && booking.photo_urls.length > 0 && (
                            <div className="flex gap-2 mt-3" data-testid={`booking-photos-${booking.id}`}>
                              {booking.photo_urls.slice(0, 5).map((url, i) => (
                                <a
                                  key={i}
                                  href={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block w-14 h-14 rounded-md overflow-hidden border border-slate-200 hover:ring-2 ring-orange-300"
                                >
                                  <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                                    alt="Booking photo"
                                    className="w-full h-full object-cover"
                                  />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="text-right">
                            <p className="text-sm text-slate-500">Callout Fee</p>
                            <p className="text-lg font-bold text-orange-500">
                              XCD ${booking.callout_fee?.toFixed(2)}
                            </p>
                          </div>
                          {booking.status === "pending" && (
                            <Button
                              asChild
                              size="sm"
                              className="bg-slate-900 hover:bg-slate-800 text-white"
                            >
                              <Link to={`/payment?booking_id=${booking.id}`}>
                                Pay Now
                              </Link>
                            </Button>
                          )}
                          {(booking.status === "completed" || booking.status === "paid") && (
                            <Button
                              asChild
                              size="sm"
                              variant="outline"
                              data-testid={`book-again-btn-${booking.id}`}
                            >
                              <Link to={`/booking?service=${booking.service_id}&rebook=1`}>
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Book Again
                              </Link>
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
              </div>
            ) : invoices.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No invoices yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <Card key={invoice.id} className="bg-white" data-testid={`invoice-${invoice.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-mono font-semibold text-slate-900">
                              {invoice.invoice_number}
                            </h3>
                            <Badge className={getStatusColor(invoice.status)}>
                              {invoice.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mb-1">{invoice.description}</p>
                          <p className="text-xs text-slate-500">Due: {invoice.due_date}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-slate-500">Amount Due</p>
                            <p className="text-lg font-bold text-slate-900">
                              XCD ${(invoice.amount - invoice.amount_paid).toFixed(2)}
                            </p>
                            {invoice.amount_paid > 0 && (
                              <p className="text-xs text-green-600">
                                Paid: XCD ${invoice.amount_paid.toFixed(2)}
                              </p>
                            )}
                          </div>
                          {invoice.status !== "paid" && (
                            <Button
                              asChild
                              size="sm"
                              className="bg-orange-500 hover:bg-orange-600 text-white"
                            >
                              <Link to={`/pay-invoice?invoice=${invoice.invoice_number}`}>
                                Pay Invoice
                              </Link>
                            </Button>
                          )}
                          <Button
                            asChild
                            size="sm"
                            variant="outline"
                            className="border-slate-300"
                          >
                            <Link to={`/view-invoice?invoice=${invoice.invoice_number}`}>
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Link>
                          </Button>
                          <Button
                            asChild
                            size="sm"
                            variant="outline"
                            className="border-slate-300"
                            data-testid={`invoice-pdf-${invoice.invoice_number}`}
                          >
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoice.invoice_number}/print`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </a>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Recurring Tab */}
          <TabsContent value="recurring">
            {recurringBookings.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center">
                  <RefreshCw className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">No recurring services set up</p>
                  <Button asChild className="bg-orange-500 hover:bg-orange-600 text-white">
                    <Link to="/booking">Set Up Recurring Service</Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {recurringBookings.map((booking) => (
                  <Card key={booking.id} className="bg-white">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-slate-900">{booking.service_name}</h3>
                            <Badge className="bg-purple-100 text-purple-700">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              {booking.recurring_frequency}
                            </Badge>
                          </div>
                          <div className="text-sm text-slate-600">
                            <p>Next Service: {booking.next_service_date || "N/A"}</p>
                            <p className="text-slate-500">{booking.address}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Callout Fee</p>
                          <p className="text-lg font-bold text-orange-500">
                            XCD ${booking.callout_fee?.toFixed(2)}/visit
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div className="space-y-6">
              {/* Reviewable bookings (CTA) */}
              <Card className="bg-white" data-testid="reviews-pending-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-orange-500" />
                    Share Your Experience
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Reviews can only be submitted for completed services and require admin
                    approval before they appear publicly.
                  </p>
                </CardHeader>
                <CardContent>
                  {reviewableBookings.length === 0 ? (
                    <p className="text-slate-500 text-sm py-4" data-testid="reviews-empty-state">
                      You don't have any completed services awaiting a review.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {reviewableBookings.map((b) => (
                        <div
                          key={b.id}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50"
                          data-testid={`reviewable-${b.id}`}
                        >
                          <div>
                            <p className="font-semibold text-slate-900">{b.service_name}</p>
                            <p className="text-xs text-slate-500">
                              Completed • {b.preferred_date}
                            </p>
                          </div>
                          <Button
                            onClick={() => openReviewDialog(b)}
                            size="sm"
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid={`leave-review-btn-${b.id}`}
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Leave a Review
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Submitted reviews */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Your Submitted Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  {reviews.length === 0 ? (
                    <p className="text-slate-500 text-sm py-4">No reviews submitted yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {reviews.map((r) => (
                        <div
                          key={r.id}
                          className="p-4 rounded-lg border border-slate-200"
                          data-testid={`my-review-${r.id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i < r.rating
                                      ? "fill-orange-400 text-orange-400"
                                      : "text-slate-300"
                                  }`}
                                />
                              ))}
                            </div>
                            <Badge
                              className={
                                r.is_approved
                                  ? "bg-green-100 text-green-700"
                                  : "bg-yellow-100 text-yellow-700"
                              }
                            >
                              {r.is_approved ? "Approved" : "Pending approval"}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-700">{r.comment}</p>
                          <p className="text-xs text-slate-400 mt-2">
                            {r.service_name} • {new Date(r.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Review Submission Dialog */}
        <Dialog open={!!reviewBooking} onOpenChange={(open) => !open && setReviewBooking(null)}>
          <DialogContent className="max-w-md" data-testid="review-dialog">
            <DialogHeader>
              <DialogTitle>Review: {reviewBooking?.service_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Your rating</p>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      type="button"
                      key={n}
                      onClick={() => setReviewRating(n)}
                      className="p-1"
                      data-testid={`review-star-${n}`}
                    >
                      <Star
                        className={`w-7 h-7 transition-colors ${
                          n <= reviewRating
                            ? "fill-orange-400 text-orange-400"
                            : "text-slate-300 hover:text-orange-200"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">
                  Tell others about your experience
                </p>
                <Textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="What went well? Would you recommend us?"
                  rows={5}
                  data-testid="review-comment-input"
                />
              </div>
              <p className="text-xs text-slate-500">
                Reviews are visible publicly only after admin approval.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setReviewBooking(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={submitReview}
                  disabled={submittingReview}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="submit-review-btn"
                >
                  {submittingReview ? "Submitting..." : "Submit Review"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
